import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { fetchMarketplaceCategories, type MarketplaceCategory } from '@/lib/marketplaceCatalog';

// ─────────────────────────────────────────────────────────────
// Catégories de la marketplace (table `categorie` du marchand).
// Chaque carte mène à la page des vendeurs de cette catégorie.
// ─────────────────────────────────────────────────────────────

export default function MarketplaceCategoriesTag() {
  const { tenant } = useTenant();
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenant?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchMarketplaceCategories(Number(tenant.id));
        if (!cancelled) setCategories(list.filter((c) => !c.parent_id));
      } catch {
        // silencieux
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenant?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {categories.map((cat) => {
        const img = resolveMediaUrl(cat.image_couverture);
        return (
          <Link
            key={cat.id}
            to={`/marketplace/categorie/${cat.id}`}
            className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-background-300/60 hover:bg-background-100/50 transition-colors cursor-pointer no-underline group"
          >
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden group-hover:bg-primary-200/70 transition-colors">
              {img ? (
                <img src={img} alt={cat.nom} className="w-full h-full object-cover" />
              ) : (
                <i className={`${cat.icon || 'ri-folder-line'} text-lg text-primary-600`}></i>
              )}
            </div>
            <span className="text-xs font-medium text-foreground-700 capitalize text-center line-clamp-1">
              {cat.nom}
            </span>
          </Link>
        );
      })}
    </div>
  );
}